import { useEffect, useState } from 'react';
import { useApiClient } from '@/lib/api';
import { fetchSignatureGraphicGenesis, type GraphicGenesisData } from '@/lib/case_signatures';

export interface SignatureGraphicGenesisState {
  data: GraphicGenesisData | null;
  /** true = backend respondeu 422 (imagem sem base para marcadores) —
   * estado "indisponível" honesto, distinto de loading/erro (ADR-0037 §F). */
  unavailable: boolean;
  /** true = falha técnica (rede/autenticação/5xx) — NUNCA apresentada como
   * "indisponível" (classe F-E26.F3-02, corrigida aqui na E26.F4.T05). */
  error: boolean;
  loading: boolean;
}

const IDLE: SignatureGraphicGenesisState = {
  data: null,
  unavailable: false,
  error: false,
  loading: false,
};

/**
 * Fetch autenticado da gênese gráfica (E26.F2/ADR-0037). Espelha
 * `useSignatureTopography` (payload JSON em vez de object URL — sem
 * createObjectURL/revoke).
 */
export function useSignatureGraphicGenesis(
  caseId: string,
  signatureId: string | undefined,
): SignatureGraphicGenesisState {
  const client = useApiClient();
  const [state, setState] = useState<SignatureGraphicGenesisState>(IDLE);

  useEffect(() => {
    if (!signatureId || !caseId) {
      setState(IDLE);
      return;
    }
    const resolvedSignatureId = signatureId;
    let cancelled = false;
    setState({ data: null, unavailable: false, error: false, loading: true });
    async function load() {
      try {
        const data = await fetchSignatureGraphicGenesis(client, caseId, resolvedSignatureId);
        if (cancelled) return;
        if (data === null) {
          setState({ data: null, unavailable: true, error: false, loading: false });
          return;
        }
        setState({ data, unavailable: false, error: false, loading: false });
      } catch {
        if (!cancelled) setState({ data: null, unavailable: false, error: true, loading: false });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [client, caseId, signatureId]);

  return state;
}
